import { useState, type FormEvent } from 'react';

import { Button } from '@/components/Button/Button';
import { Callout } from '@/components/Callout/Callout';
import { Dialog } from '@/components/Dialog/Dialog';
import { Select } from '@/components/Select/Select';
import { TextField } from '@/components/TextField/TextField';
import { COUNTRIES, isValidKey, keyFromName } from '@/lib/lenders';

import styles from './LenderForm.module.css';

export interface LenderDraft {
  key: string;
  name: string;
  country: string;
}

export interface LenderFormProps {
  /** `create` asks for a reference too; it can never change afterwards. */
  mode: 'create' | 'edit';
  initial?: Partial<LenderDraft>;
  busy?: boolean;
  error?: string | null;
  onSubmit: (draft: LenderDraft) => void;
  onCancel: () => void;
}

/** Add a lender, or change its name and country. */
export function LenderForm({
  mode,
  initial = {},
  busy = false,
  error = null,
  onSubmit,
  onCancel,
}: LenderFormProps) {
  const [name, setName] = useState(initial.name ?? '');
  const [country, setCountry] = useState(initial.country ?? 'IE');
  const [key, setKey] = useState(initial.key ?? '');
  const [keyEdited, setKeyEdited] = useState(Boolean(initial.key));
  const [touched, setTouched] = useState(false);

  const effectiveKey = keyEdited ? key : keyFromName(name);
  const nameError = touched && !name.trim() ? 'Give the lender a name.' : undefined;
  const keyError =
    mode === 'create' && touched && !isValidKey(effectiveKey)
      ? 'Use lowercase letters, numbers and underscores, starting with a letter.'
      : undefined;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setTouched(true);
    if (!name.trim() || (mode === 'create' && !isValidKey(effectiveKey))) {
      return;
    }
    onSubmit({ key: effectiveKey, name: name.trim(), country });
  };

  const formId = `lender-form-${mode}`;
  return (
    <Dialog
      actions={
        <>
          <Button disabled={busy} onClick={onCancel} variant="ghost">
            Cancel
          </Button>
          <Button form={formId} loading={busy} type="submit">
            {mode === 'create' ? 'Add lender' : 'Save changes'}
          </Button>
        </>
      }
      description={
        mode === 'create'
          ? 'New lenders are active straight away. Add their documents next.'
          : 'Changes show to clients as soon as you save.'
      }
      dismissable={!busy}
      onClose={onCancel}
      title={mode === 'create' ? 'Add a lender' : 'Edit lender'}
    >
      <form id={formId} className={styles.form} noValidate onSubmit={submit}>
        <TextField
          autoComplete="off"
          data-autofocus
          error={nameError}
          label="Lender name"
          onChange={(event) => setName(event.target.value)}
          placeholder="e.g. Bank of Ireland"
          value={name}
        />
        <Select
          label="Country"
          onChange={setCountry}
          options={
            COUNTRIES.some(([code]) => code === country)
              ? COUNTRIES
              : [[country, country], ...COUNTRIES]
          }
          value={country}
        />
        {mode === 'create' ? (
          <TextField
            autoComplete="off"
            error={keyError}
            hint="How the LendWell app recognises this lender. Filled in from the name, and it can't be changed later."
            label="Reference"
            onChange={(event) => {
              setKeyEdited(true);
              setKey(event.target.value);
            }}
            value={effectiveKey}
          />
        ) : null}
        {error ? (
          <Callout tone="danger" title="That didn't work">
            {error}
          </Callout>
        ) : null}
      </form>
    </Dialog>
  );
}
